import { Link } from "react-router-dom";
import { HeartPulse, Leaf, Truck, ArrowRight, ShieldCheck, Users, Wrench, Sprout, Scissors, Trees, Flame, BrickWall, Recycle, MapPin, BadgeCheck, Clock, Sparkles } from "lucide-react";
import { Reveal, Overline } from "@/components/public/Reveal";
import Seo, { localBusinessLd } from "@/components/Seo";
import { fileUrl } from "@/lib/api";

const certifs = [
  { icon: HeartPulse, title: "Brevet de Secourisme", desc: "Nos équipes suivent une formation annuelle : une personne par équipe est toujours capable de dispenser les premiers soins en attendant les secours." },
  { icon: Leaf, title: "Agrément Phytosanitaire", desc: "L'ensemble de nos ouvriers dispose de cet agrément obligatoire pour utiliser les produits phytosanitaires en toute sécurité sanitaire." },
  { icon: ShieldCheck, title: "CACES", desc: "Quatre de nos ouvriers sont titulaires du CACES : la garantie d'une conduite d'engins en sécurité, primordiale dans notre métier." },
];

const engins = [
  { img: "acanthe-jardins/uploads/d25af266-c12b-4117-b895-095851e76d50.png", name: "Mini-chargeur Caterpillar S130", specs: "2,5 t · 1,5 m de large · godet 200 L + godet malaxeur" },
  { img: "acanthe-jardins/uploads/59001cc5-0a20-4aec-bddd-1a866fb5c5f0.png", name: "Mini-pelles 1,5 t & 2,5 t", specs: "Godets 30, 40 et 60 cm · équipées d'un BRH (brise-roche hydraulique)" },
  { img: "acanthe-jardins/uploads/8f24368a-8f98-4699-bfb8-853488c4bbc2.png", name: "Ditch Witch multi-équipements", specs: "8 équipements : trancheuse, tarière, godet 390 L, fourches à palette, griffe de décompactage, préparateur de sol, plateau niveleur, rogneuse de souche" },
  { img: "acanthe-jardins/uploads/c9ec9a0e-ae22-4c05-b1f7-da9642d37463.png", name: "Tractopelle JCB 3CX", specs: "Gros terrassement et déplacement de sujets importants" },
  { img: "acanthe-jardins/uploads/efa0c403-464e-43b0-a1b4-729e20970a43.png", name: "Camion 8x4 avec grue", specs: "Transport et mise en place de matériaux dans les accès difficiles", wide: true },
];

const vehicules = [
  "1 camion double cabine 3,5 t",
  "3 camions simple cabine 3,5 t",
  "1 camion 8x4 équipé d'une grue",
  "1 fourgon Nissan (direction & conduite de travaux)",
];

const expertises = [
  {
    icon: Sprout, title: "Création & aménagement paysager",
    points: [
      "Conception de jardins sur mesure tenant compte de vos envies, des contraintes techniques et des spécificités climatiques locales",
      "Terrassement, fond de forme, nivellement, épierrage",
      "Plantations : arbustes, arbres, vivaces et massifs",
      "Paillage : minéral & végétal",
      "Engazonnement : semis, gazon en plaques ou hydroseeding",
      "Systèmes d'arrosage automatisé",
    ],
  },
  {
    icon: Scissors, title: "Entretien d'espaces verts",
    points: [
      "Contrats annuels ou interventions ponctuelles",
      "Tonte, taille de haies et d'arbustes, désherbage",
      "Entretien des massifs, ramassage de feuilles, remise en état",
      "Diagnostic végétal",
      "Traitement phytosanitaire",
    ],
  },
  {
    icon: Trees, title: "Élagage, abattage & soins aux arbres",
    points: [
      "Élagage raisonné et taille de formation ou de sécurisation",
      "Abattage d'arbres dangereux ou malades",
      "Interventions dans le respect des règles de sécurité et de l'environnement",
    ],
  },
  {
    icon: Flame, title: "Déboisage & débroussaillage",
    points: [
      "Déboisage réglementaire pour la prévention des incendies (normes OLD)",
      "Débroussaillage & gyrobroyage",
      "Création de pistes DFCI",
    ],
  },
  {
    icon: BrickWall, title: "Aménagements extérieurs & maçonnerie paysagère",
    points: [
      "Création d'allées (graviers, stabilisés, dallage)",
      "Pose de clôtures, portails",
      "Réalisation de terrasses (bois ou minéral), bordures et petits ouvrages de maçonnerie",
    ],
  },
  {
    icon: Recycle, title: "Gestion des déchets verts",
    points: [
      "Démarche respectueuse de l'environnement",
      "Broyage, valorisation des déchets et évacuation",
    ],
  },
];

const engagements = [
  { icon: MapPin, title: "Expertise locale", desc: "Parfaite connaissance du terrain, des sols et du climat méditerranéen." },
  { icon: BadgeCheck, title: "Qualité & suivi", desc: "Travail soigné, conseils personnalisés et suivi rigoureux de chaque chantier." },
  { icon: Clock, title: "Proximité & réactivité", desc: "Une équipe disponible et attentive à vos besoins." },
  { icon: Sprout, title: "Solutions durables", desc: "Des aménagements adaptés pour la pérennité de vos espaces verts." },
];

export default function Entreprise() {
  return (
    <div>
      <Seo title="L'entreprise, nos expertises & notre matériel" path="/entreprise"
        description="Acanthe Jardins : aménagement & entretien des espaces extérieurs pour particuliers, professionnels, collectivités et structures publiques. 6 domaines d'expertise, 14 salariés qualifiés et un parc matériel complet dans les Bouches-du-Rhône, le Var et le Vaucluse."
        jsonLd={localBusinessLd()} />

      {/* Intro */}
      <section className="pt-32 md:pt-40 pb-16 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Overline>L'entreprise</Overline>
            <h1 className="mt-4 font-display text-5xl lg:text-6xl text-forest leading-[1.02]">Un savoir-faire humain, des moyens à la hauteur de vos projets</h1>
            <p className="mt-6 text-lg text-stone leading-relaxed">
              Fondée il y a plus de 14 ans dans les Bouches-du-Rhône, Acanthe Jardins a élargi son activité au Var et au Vaucluse. Notre savoir-faire couvre tout l'aménagement extérieur, du terrassement à la création d'espaces verts, dans le respect de nos engagements qualité et de gestion.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Particuliers", "Professionnels", "Collectivités", "Structures publiques"].map((s) => (
                <span key={s} className="rounded-full border border-brand/30 bg-brand/5 px-4 py-1.5 text-sm font-medium text-brand">{s}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="grid grid-cols-3 gap-4">
            {[{ n: "14", l: "Salariés qualifiés" }, { n: "14+", l: "Ans d'expérience" }, { n: "3", l: "Départements" }].map((s) => (
              <div key={s.l} className="rounded-2xl bg-white border border-border p-6 text-center">
                <p className="font-display text-5xl text-brand">{s.n}</p>
                <p className="text-xs uppercase tracking-wider text-stone mt-2">{s.l}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Nos expertises */}
      <section className="py-20 md:py-28 bg-[#F0EFEA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl mb-14">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-terracotta"><Sparkles size={14} /> Nos expertises</span>
            <h2 className="mt-4 font-display text-4xl lg:text-5xl text-forest leading-tight">Un métier complet, de la création à la gestion</h2>
            <p className="mt-4 text-stone">Six domaines d'expertise pour accompagner tous vos projets d'aménagement et d'entretien des espaces extérieurs.</p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6">
            {expertises.map((e, i) => (
              <Reveal key={e.title} delay={(i % 2) * 0.08}>
                <div className="group h-full rounded-3xl bg-white border border-border overflow-hidden hover:shadow-xl hover:shadow-forest/5 transition-all duration-300" data-testid={`expertise-${i}`}>
                  <div className="flex items-center gap-4 bg-forest px-6 py-5">
                    <div className="h-12 w-12 rounded-2xl bg-cream/10 flex items-center justify-center shrink-0"><e.icon className="text-flora" size={24} /></div>
                    <h3 className="font-display text-xl md:text-2xl text-cream leading-tight">{e.title}</h3>
                  </div>
                  <ul className="p-6 space-y-2.5">
                    {e.points.map((p) => (
                      <li key={p} className="flex gap-3 text-sm text-stone leading-relaxed">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-terracotta shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Notre engagement */}
      <section className="py-20 md:py-28 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl mb-12">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-terracotta"><BadgeCheck size={14} /> Notre engagement</span>
            <h2 className="mt-4 font-display text-4xl lg:text-5xl text-forest">Ce sur quoi vous pouvez compter</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {engagements.map((e, i) => (
              <Reveal key={e.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl bg-white border border-border p-7" data-testid={`engagement-${i}`}>
                  <div className="h-11 w-11 rounded-xl bg-brand flex items-center justify-center mb-4"><e.icon className="text-cream" size={20} /></div>
                  <h3 className="font-display text-xl text-forest mb-2">{e.title}</h3>
                  <p className="text-sm text-stone leading-relaxed">{e.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Moyens humains / certifs */}
      <section className="py-20 md:py-28 bg-[#F0EFEA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl mb-12">
            <Overline><span className="inline-flex items-center gap-2"><Users size={14} /> Moyens humains</span></Overline>
            <h2 className="mt-4 font-display text-4xl lg:text-5xl text-forest">Des équipes formées et certifiées</h2>
            <p className="mt-4 text-stone">Nous accordons une forte exigence au recrutement : chaque salarié possède une ou plusieurs qualifications.</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {certifs.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.1}>
                <div className="group relative h-full rounded-2xl bg-cream border border-border p-8 overflow-hidden hover:-translate-y-1.5 hover:shadow-xl hover:shadow-forest/5 transition-all duration-300" data-testid={`certif-${i}`}>
                  <span className="absolute -top-2 right-4 font-display text-7xl text-forest/[0.06] select-none">0{i + 1}</span>
                  <div className="relative flex items-center gap-3 mb-5">
                    <div className="h-12 w-12 rounded-xl bg-forest flex items-center justify-center"><c.icon className="text-cream" size={22} /></div>
                    <span className="h-px flex-1 bg-gradient-to-r from-terracotta/60 to-transparent" />
                  </div>
                  <h3 className="relative font-display text-2xl text-forest mb-2">{c.title}</h3>
                  <p className="relative text-sm text-stone leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Parc matériel — engins (masqué à la demande du client) */}
      {false && (
      <section id="materiel" className="relative py-20 md:py-28 bg-forest overflow-hidden scroll-mt-20">
        <div className="absolute inset-0 blueprint-grid opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl mb-14">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-flora"><Wrench size={14} /> Notre parc matériel</span>
            <h2 className="mt-4 font-display text-4xl lg:text-5xl text-cream leading-tight">Des engins professionnels pour chaque chantier</h2>
            <p className="mt-4 text-cream/70">Un matériel complet et entretenu qui nous permet d'intervenir sur tous types de terrains, même les accès les plus difficiles.</p>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {engins.map((e, i) => (
              <Reveal key={e.name} delay={(i % 3) * 0.08} className={e.wide ? "md:col-span-2" : ""}>
                <div data-testid={`engin-${i}`}
                  className="group relative h-full rounded-[26px] bg-cream border border-white/10 p-3 flex flex-col hover:-translate-y-1.5 transition-transform duration-300">
                  <div className="relative rounded-[18px] overflow-hidden bg-gradient-to-br from-white via-white to-sage blueprint-grid-light">
                    <span className="absolute top-3 left-4 z-10 font-display text-3xl text-forest/15">0{i + 1}</span>
                    <span className="absolute top-4 right-4 z-10 h-2.5 w-2.5 rounded-full bg-flora shadow-[0_0_12px_2px_rgba(46,184,46,0.5)]" />
                    <img src={fileUrl(e.img)} alt={e.name}
                      className={`relative mx-auto object-contain transition-transform duration-500 group-hover:scale-[1.04] ${e.wide ? "max-h-56" : "max-h-48"}`} />
                  </div>
                  <div className="px-4 pt-5 pb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="h-px w-6 bg-terracotta" />
                      <h3 className="font-display text-2xl text-forest leading-none">{e.name}</h3>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {e.specs.split(/ · |, /).map((s, k) => (
                        <span key={k} className="rounded-full bg-sage/70 px-3 py-1 text-[12px] text-forest/80 leading-tight">{s}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}

            {/* Véhicules card */}
            <Reveal delay={0.1}>
              <div className="relative h-full rounded-[26px] bg-terracotta text-cream p-8 flex flex-col overflow-hidden" data-testid="vehicules-card">
                <div className="absolute inset-0 blueprint-grid opacity-40" />
                <Truck size={34} className="relative mb-4" />
                <h3 className="relative font-display text-3xl mb-5">Nos véhicules</h3>
                <ul className="relative space-y-2.5 text-sm text-cream/95">
                  {vehicules.map((v) => (
                    <li key={v} className="flex gap-2.5"><span className="text-cream/60 font-bold">+</span> {v}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      )}

      {/* Pépinière / livraison */}
      <section className="py-20 md:py-28 bg-cream">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <Overline>Le saviez-vous ?</Overline>
            <h2 className="mt-4 font-display text-4xl lg:text-5xl text-forest">Nous livrons tous vos végétaux, du godet au spécimen rare</h2>
            <p className="mt-6 text-lg text-stone leading-relaxed">
              Notre société est capable de vous livrer directement chez vous tout type de végétaux, du plus petit au plus grand. Quel que soit l'accès, du plus simple au plus compliqué, nous adaptons une manutention et un conditionnement sur-mesure à chaque situation.
            </p>
            <Link to="/contact" data-testid="entreprise-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-semibold text-cream hover:bg-forest transition-colors">
              Demander un devis <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
